/**
 * Numerical formatting utilities for Patterns Radar module.
 * Prevents floating-point precision overflow (e.g. +3.1030444964871284% -> +3.10%).
 */

export const toNum = (val) => {
  if (val === null || val === undefined || val === '') return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
};

/**
 * Formats a percentage value with max 2 decimals.
 * e.g. 3.10304449 -> "+3.10%" (if showPlus = true)
 * e.g. -3.670477 -> "-3.67%"
 * e.g. 0 -> "0.00%"
 */
export const formatPercent = (
  val,
  { decimals = 2, showPlus = true, fallback = '-' } = {}
) => {
  const n = toNum(val);
  if (n === null) return fallback;
  const fixed = Math.abs(n) < 1e-9 ? (0).toFixed(decimals) : n.toFixed(decimals);
  if (showPlus && Number(fixed) > 0) {
    return `+${fixed}%`;
  }
  return `${fixed}%`;
};

/**
 * Formats currency values in Indian numbering system (e.g. ₹1,761 or ₹1,761.50)
 */
export const formatCurrency = (
  val,
  { decimals = 2, fallback = '-' } = {}
) => {
  const n = toNum(val);
  if (n === null) return fallback;
  return (
    '₹' +
    n.toLocaleString('en-IN', {
      maximumFractionDigits: decimals,
    })
  );
};

/**
 * Formats a multiplier (e.g. 1.84928 -> "1.85x")
 */
export const formatMultiplier = (
  val,
  { decimals = 2, suffix = 'x', fallback = '-' } = {}
) => {
  const n = toNum(val);
  if (n === null) return fallback;
  return `${n.toFixed(decimals)}${suffix}`;
};

/**
 * Formats a decimal number cleanly with fixed precision
 */
export const formatDecimal = (
  val,
  { decimals = 2, fallback = '-' } = {}
) => {
  const n = toNum(val);
  if (n === null) return fallback;
  return n.toFixed(decimals);
};

/**
 * Parses a range or comparison string (e.g. "100-500", ">=100", "<=500", "100+", "100")
 * Matches the numeric range filter pattern from the Subscriber module.
 */
export const parseNumericRange = (value) => {
  if (!value || typeof value !== 'string') return { min: undefined, max: undefined };
  const trimmed = value.trim();
  if (!trimmed) return { min: undefined, max: undefined };

  // Match range like "100-200", "100 - 200", "100 to 200"
  const rangeMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*(?:-|to)\s*(\d+(?:\.\d+)?)$/i);
  if (rangeMatch) {
    return { min: parseFloat(rangeMatch[1]), max: parseFloat(rangeMatch[2]), isRange: true };
  }

  // Match ">=100", ">100", "100+"
  const gteMatch = trimmed.match(/^(?:>=?|>)\s*(\d+(?:\.\d+)?)$/) || trimmed.match(/^(\d+(?:\.\d+)?)\+$/);
  if (gteMatch) {
    return { min: parseFloat(gteMatch[1]), max: undefined, isGte: true };
  }

  // Match "<=200", "<200"
  const lteMatch = trimmed.match(/^(?:<=?|<)\s*(\d+(?:\.\d+)?)$/);
  if (lteMatch) {
    return { min: undefined, max: parseFloat(lteMatch[1]), isLte: true };
  }

  // Single number "100": match integer or close
  const singleMatch = trimmed.match(/^(\d+(?:\.\d+)?)$/);
  if (singleMatch) {
    const val = parseFloat(singleMatch[1]);
    return { min: val, max: val, isSingle: true };
  }

  return { min: undefined, max: undefined };
};

/**
 * Checks whether a given numeric price matches a range filter string.
 */
export const matchesNumericRange = (priceVal, filterStr) => {
  if (!filterStr || !filterStr.trim()) return true;
  const price = toNum(priceVal);
  if (price === null || isNaN(price)) return false;

  const { min, max, isSingle } = parseNumericRange(filterStr);
  if (min === undefined && max === undefined) return true;

  if (isSingle) {
    if (
      Math.abs(price - min) > (min === 0 ? 0.01 : min * 0.05) &&
      Math.floor(price) !== Math.floor(min)
    ) {
      return false;
    }
    return true;
  }

  if (min !== undefined && price < min) return false;
  if (max !== undefined && price > max) return false;
  return true;
};

